"use client";

import { ArrowLeft, ArrowRight, Calendar, Instagram, Linkedin, Tag, User, ChevronRight, Clock, Bookmark } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-8 py-20">
        <div className="h-4 w-32 bg-white/5 animate-pulse rounded-lg" />
        <div className="h-16 bg-white/5 animate-pulse rounded-lg" />
        <div className="h-96 bg-white/5 animate-pulse rounded-lg" />
        <div className="space-y-4">
          <div className="h-4 bg-white/5 animate-pulse w-full rounded-lg" />
          <div className="h-4 bg-white/5 animate-pulse w-5/6 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="max-w-3xl mx-auto text-center py-32">
        <p className="text-white/40 font-sans">Project details could not be retrieved.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="relative">
      {}
      <header className="max-w-3xl mx-auto mb-16">
        <nav className="mb-12 flex items-center gap-2 text-sm font-mono uppercase tracking-[0.2em] text-[#EAB308]">
          <Link to="/BlogPage" className="hover:opacity-70 transition-opacity flex items-center gap-2">
            <ArrowLeft className="size-4" />
            BLOG
          </Link>
          <ChevronRight className="size-3 text-white/20" />
          <span className="text-white/40 truncate" dangerouslySetInnerHTML={{ __html: post.title }} />
        </nav>

        <div className="flex items-center gap-3 mb-6">
          <span className="bg-[#EAB308] text-black px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest rounded-md">
            Featured
          </span>
          <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">
            § 01 // CASE STUDY
          </span>
        </div>

        <h1
          className="font-default font-extrabold text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-8 text-white"
          dangerouslySetInnerHTML={{ __html: post.title }}
        />

        <div className="flex flex-wrap items-center justify-between gap-6 pt-8 border-t border-white/10">
          <div className="flex items-center gap-6">
            {post.author && (
              <div className="flex items-center gap-3">
                <Avatar className="size-10 rounded-lg border border-white/10">
                  <AvatarImage src={post.author.avatar_url || post.author.photo || '/wp-content/uploads/default-avatar.png'} alt={post.author.name} />
                  <AvatarFallback className="bg-white/5 text-white text-xs rounded-lg">
                    {post.author.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm font-bold text-white">{post.author.name}</div>
                </div>
              </div>
            )}
            {formattedDate && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">DATA PUBLISHED</div>
                <div className="text-sm font-bold text-white">{formattedDate}</div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(post.link || "")}`} newTab>
              <Button variant="outline" size="icon" className="rounded-lg border-white/10 hover:border-[#EAB308] hover:text-[#EAB308]">
                <Linkedin className="size-4" />
              </Button>
            </Link>
            <Link to="https://www.instagram.com/marinela_architects" newTab>
              <Button variant="outline" size="icon" className="rounded-lg border-white/10 hover:border-[#EAB308] hover:text-[#EAB308]">
                <Instagram className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {post.author && (
        <div className="max-w-3xl mx-auto mb-16 p-8 md:p-12 bg-white/5 rounded-lg border border-white/10">
          <div className="flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
            <img 
              src={post.author.avatar_url || post.author.photo || '/wp-content/uploads/default-avatar.png'} 
              data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/Screenshot 2025-05-13 alle 11.50.28.png-wvc-srcset"
              data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/Screenshot 2025-05-13 alle 11.50.28.png-wvc-sizes"
              className="size-24 rounded-lg object-cover object-top border-4 border-[#EAB308]" 
              alt={post.author.name}
              loading="lazy"
            />
            <div className="flex-1">
              <h3 className="font-default font-bold text-2xl mb-4 text-white">{post.author.name}</h3>
              {formattedDate && (
                <div className="mb-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">DATA PUBLISHED</div>
                  <div className="text-sm font-bold text-white">{formattedDate}</div>
                </div>
              )}
              <p className="font-serif text-lg text-white/60 mb-6 leading-relaxed">
                {post.author.name} - Founder di Lionia Project
              </p>
              <div className="flex flex-wrap gap-3 font-mono uppercase text-[10px]">
                <Link to="https://instagram.com/lionia_project" newTab>
                  <Button variant="outline" size="sm" className="font-mono uppercase tracking-widest text-[10px] rounded-lg border-white/20">
                    Segui @lionia_project
                  </Button>
                </Link>
                <Link to="mailto:marinela.demrozi@gmail.com">
                  <Button variant="outline" size="sm" className="font-mono uppercase tracking-widest text-[10px] rounded-lg border-white/20">
                    Supporta il progetto
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {post.featuredImage && (
        <div className="mb-20">
          <div className="relative aspect-[21/9] overflow-hidden rounded-lg">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/40 to-transparent" />
          </div>
          <p className="mt-4 text-center text-[10px] font-mono uppercase tracking-widest text-white/30">
            Visual documentation of the {post.title} project phase.
          </p>
        </div>
      )}

      {}
      <div className="max-w-3xl mx-auto">
        <div
          className="prose prose-invert max-w-none font-serif text-xl md:text-2xl leading-relaxed text-white/90 
          [&>a]:text-[#EAB308] [&>a]:hover:text-[#EAB308]/80 [&>a]:underline [&>a]:decoration-[#EAB308]/30 [&>a]:underline-offset-2
          [&>p]:mb-10 
          [&>p:first-of-type]:first-letter:text-7xl [&>p:first-of-type]:first-letter:font-default [&>p:first-of-type]:first-letter:font-extrabold [&>p:first-of-type]:first-letter:text-[#EAB308] [&>p:first-of-type]:first-letter:mr-3 [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:leading-none
          [&>h2]:font-default [&>h2]:font-bold [&>h2]:text-4xl [&>h2]:mt-20 [&>h2]:mb-8 [&>h2]:text-white [&>h2]:tracking-tight
          [&>h3]:font-default [&>h3]:font-bold [&>h3]:text-2xl [&>h3]:mt-16 [&>h3]:mb-6 [&>h3]:text-white
          [&>blockquote]:border-l-0 [&>blockquote]:pl-0 [&>blockquote]:my-20 [&>blockquote]:text-center [&>blockquote]:italic [&>blockquote]:text-3xl [&>blockquote]:md:text-4xl [&>blockquote]:font-default [&>blockquote]:font-bold [&>blockquote]:text-white [&>blockquote]:leading-tight
          [&>ul]:list-none [&>ul]:pl-0 [&>ul>li]:relative [&>ul>li]:pl-6 [&>ul>li]:mb-4 [&>ul>li]:before:content-[''] [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:top-3 [&>ul>li]:before:size-1.5 [&>ul>li]:before:bg-[#EAB308] [&>ul>li]:before:rounded-lg
          [&_img]:my-20 [&_img]:rounded-lg [&_img]:border [&_img]:border-white/10"
          dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
        />

        {}
        <footer className="mt-24 pt-12 border-t border-white/10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, i) => (
                  <span
                    data-index={i}
                    key={tag.id}
                    className="px-3 py-1 text-[10px] font-mono uppercase tracking-widest bg-white/5 text-white/60 rounded-lg hover:bg-[#EAB308] hover:text-black transition-colors cursor-default"
                  >
                    #{tag.name}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-4">
              <Button variant="outline" className="font-mono text-[10px] uppercase tracking-widest rounded-lg border-white/10">
                <Clock className="size-3 mr-2 text-[#EAB308]" />
                8 Min Read
              </Button>
              <Button variant="outline" className="font-mono text-[10px] uppercase tracking-widest rounded-lg border-white/10">
                <Tag className="size-3 mr-2 text-[#EAB308]" />
                Archive
              </Button>
            </div>
          </div>

          <div className="mt-12 flex items-center justify-center gap-4 py-8 border-y border-white/5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">Share Project:</span>
            <Link to={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(post.link || "")}`} newTab>
              <Button variant="outline" size="sm" className="rounded-lg border-white/10 hover:border-[#EAB308] hover:text-[#EAB308] font-mono text-[10px] uppercase tracking-widest">
                <Linkedin className="size-3 mr-2" />
                LinkedIn
              </Button>
            </Link>
            <Link to="https://www.instagram.com/marinela_architects" newTab>
              <Button variant="outline" size="sm" className="rounded-lg border-white/10 hover:border-[#EAB308] hover:text-[#EAB308] font-mono text-[10px] uppercase tracking-widest">
                <Instagram className="size-3 mr-2" />
                Instagram
              </Button>
            </Link>
          </div>

          {}
          {post.author && (
            <div className="mt-20 p-8 md:p-12 bg-white/5 rounded-lg border border-white/10">
              <div className="flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
                <img 
                  src={post.author.avatar_url || post.author.photo || '/wp-content/uploads/default-avatar.png'} 
                  data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/Screenshot 2025-05-13 alle 11.50.28.png-wvc-srcset"
                  data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/Screenshot 2025-05-13 alle 11.50.28.png-wvc-sizes"
                  className="size-24 rounded-lg object-cover object-top border-4 border-[#EAB308]" 
                  alt={post.author.name}
                  loading="lazy"
                />
                <div className="flex-1">
                  <h3 className="font-default font-bold text-2xl mb-4 text-white">{post.author.name}</h3>
                  {formattedDate && (
                    <div className="mb-4">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">DATA PUBLISHED</div>
                      <div className="text-sm font-bold text-white">{formattedDate}</div>
                    </div>
                  )}
                  <p className="font-serif text-lg text-white/60 mb-6 leading-relaxed">
                    {post.author.name} - Founder di Lionia Project
                  </p>
                  <div className="flex flex-wrap gap-3 font-mono uppercase text-[10px]">
                    <Link to="https://instagram.com/lionia_project" newTab>
                      <Button variant="outline" size="sm" className="font-mono uppercase tracking-widest text-[10px] rounded-lg border-white/20">
                        Segui @lionia_project
                      </Button>
                    </Link>
                    <Link to="mailto:marinela.demrozi@gmail.com">
                      <Button variant="outline" size="sm" className="font-mono uppercase tracking-widest text-[10px] rounded-lg border-white/20">
                        Supporta il progetto
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </footer>
      </div>
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="aspect-video bg-white/5 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.slice(0, 3).map((post, i) => (
        <Link
          data-index={i}
          key={post.id}
          to={post.link || "#"}
          className="group block"
        >
          <Card className="h-full bg-transparent border-none p-0 gap-4">
            {post.featuredImage && (
              <div className="aspect-video overflow-hidden rounded-lg bg-white/5">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            <CardContent className="p-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[9px] font-mono uppercase tracking-widest text-[#EAB308] bg-[#EAB308]/10 px-2 py-0.5 rounded-lg">
                  Project
                </span>
                <span className="text-[9px] font-mono uppercase tracking-widest text-white/30">
                  {post.date ? new Date(post.date).toLocaleDateString("en-US", { month: 'short', year: 'numeric' }) : 'Recent'}
                </span>
              </div>
              <h3
                className="font-default font-bold text-xl leading-tight text-white group-hover:text-[#EAB308] transition-colors line-clamp-2"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

export default function Portfolio() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section 
      data-section-id="2597"
      id="single-post-template-section"
      className="relative bg-[#0A0A0A] text-white py-24 md:py-40 px-6 md:px-12 overflow-hidden"
    >
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
           style={{ backgroundImage: 'radial-gradient(circle, #EAB308 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      
      <div className="relative max-w-7xl mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-40 pt-24 border-t border-white/10">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.4em] text-[#EAB308] mb-4">
                § 02 // CURATED
              </div>
              <h2 className="font-default font-extrabold text-4xl md:text-5xl tracking-tighter text-white">
                More from MARINELA
              </h2>
            </div>
            <Link
              to="/BlogPage"
              className="hidden md:inline-flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-[#EAB308] transition-colors group"
            >
              View All Stories
              <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsContent />
          </PostsProvider>
          
          <div className="mt-12 md:hidden">
            <Link to="/BlogPage">
              <Button variant="outline" className="w-full font-mono uppercase tracking-widest text-[10px] rounded-lg border-white/10">
                View All Stories
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}